import { FC, ReactNode, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { Button } from "components/buttons/Button"
import { LabeledInput } from "components/inputs/LabeledInput"

import { Modal } from "./Modal"

interface ModalConfirmDestructiveProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  title: string
  /** What the action destroys, in the operator's own terms. */
  description: ReactNode
  /** Named consequences, rendered as a list so nothing is hidden in a paragraph. */
  consequences?: string[]
  confirmLabel: string
  /**
   * When set, the confirm button stays disabled until the operator types this exact phrase.
   * Use it for actions that reach beyond the screen the operator is looking at.
   */
  confirmPhrase?: string
  loading?: boolean
}

/**
 * Confirmation for an action that destroys configuration.
 *
 * Distinct from `ModalConfirmAction`: it states the consequences explicitly and can require
 * a typed phrase, so a wide reset cannot be dismissed with the same reflex as a save.
 */
export const ModalConfirmDestructive: FC<ModalConfirmDestructiveProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  consequences,
  confirmLabel,
  confirmPhrase,
  loading = false
}) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [typed, setTyped] = useState("")

  useEffect(() => {
    if (!open) setTyped("")
  }, [open])

  const phraseMatches = !confirmPhrase || typed.trim() === confirmPhrase

  return (
    <Modal open={open} handler={onClose}>
      <div className="modal-box max-w-lg bg-white dark:bg-[#0C0C1A]">
        <h3 className="text-lg font-semibold text-[rgba(6,7,21,1)] dark:text-white">{title}</h3>

        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">{description}</p>

        {!!consequences?.length && (
          <div className="mt-4 rounded-lg border border-error/40 bg-error/5 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-error">{t("settings.danger.willBeReset")}</p>
            <ul className="mt-2 list-disc pl-5 text-sm text-gray-700 dark:text-gray-300">
              {consequences.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {confirmPhrase && (
          <div className="mt-4">
            <LabeledInput
              id="confirm-phrase"
              label={t("settings.danger.typeToConfirm", { phrase: confirmPhrase })}
              value={typed}
              onChange={setTyped}
              placeholder={confirmPhrase}
              disabled={loading}
            />
          </div>
        )}

        <div className="modal-action">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {t("common.cancel")}
          </Button>
          <Button variant="error" onClick={onConfirm} disabled={!phraseMatches} loading={loading}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
